import { useState, useRef, useCallback, useReducer, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Stack,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import { parseDevMessageFile, parseLabCloudExportFile } from '@/lib/impedance';
import type { DevMessageFile, ParseResult, LabCloudExportFile } from '@/lib/impedance';
import { FilePairPreview, type FilePairItem } from './FilePairPreview';
import { importService } from '@/services/api/importService';
import type { ImportBatchResult } from '@/services/api/importService';
import { profileService } from '@/services/api/profileService';
import type { ProfileSummary } from '@/services/api/profileService';

// ─── Pairing state ─────────────────────────────────────────────────────────

interface PairingState {
  pairs: FilePairItem[];
  unpairedFiles: DevMessageFile[];
}

type PairingAction =
  | { type: 'init'; pairs: FilePairItem[]; unpairedFiles: DevMessageFile[] }
  | { type: 'toggle'; index: number }
  | { type: 'swap'; index: number }
  | {
      type: 'reassign';
      pairIndex: number;
      slot: 'baseline' | 'specimen';
      file: DevMessageFile;
    };

function pairingReducer(
  state: PairingState,
  action: PairingAction,
): PairingState {
  switch (action.type) {
    case 'init':
      return { pairs: action.pairs, unpairedFiles: action.unpairedFiles };

    case 'toggle': {
      const pairs = state.pairs.map((p, i) =>
        i === action.index ? { ...p, included: !p.included } : p,
      );
      return { ...state, pairs };
    }

    case 'swap': {
      const pairs = state.pairs.map((p, i) =>
        i === action.index
          ? { ...p, baseline: p.specimen, specimen: p.baseline }
          : p,
      );
      return { ...state, pairs };
    }

    case 'reassign': {
      const { pairIndex, slot, file } = action;
      const newPairs = state.pairs.map((p) => ({ ...p }));
      const newUnpaired = [...state.unpairedFiles];

      const targetPair = newPairs[pairIndex];
      const displacedFile =
        slot === 'baseline' ? targetPair.baseline : targetPair.specimen;

      // No-op if same file
      if (displacedFile.filename === file.filename) return state;

      // Remove file from its current pair slot, replacing with displacedFile
      let foundInPair = false;
      for (let i = 0; i < newPairs.length; i++) {
        if (newPairs[i].baseline.filename === file.filename) {
          newPairs[i] = { ...newPairs[i], baseline: displacedFile };
          foundInPair = true;
          break;
        }
        if (newPairs[i].specimen.filename === file.filename) {
          newPairs[i] = { ...newPairs[i], specimen: displacedFile };
          foundInPair = true;
          break;
        }
      }

      // If file was in unpairedFiles, move displacedFile there
      if (!foundInPair) {
        const unpairedIdx = newUnpaired.findIndex(
          (f) => f.filename === file.filename,
        );
        if (unpairedIdx !== -1) {
          newUnpaired.splice(unpairedIdx, 1, displacedFile);
        }
      }

      // Place file into target slot
      if (slot === 'baseline') {
        newPairs[pairIndex] = { ...newPairs[pairIndex], baseline: file };
      } else {
        newPairs[pairIndex] = { ...newPairs[pairIndex], specimen: file };
      }

      return { pairs: newPairs, unpairedFiles: newUnpaired };
    }

    default:
      return state;
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function naturalSort(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

function autoPair(
  validFiles: DevMessageFile[],
): { pairs: FilePairItem[]; unpairedFiles: DevMessageFile[] } {
  const sorted = [...validFiles].sort((a, b) =>
    naturalSort(a.filename, b.filename),
  );
  const pairs: FilePairItem[] = [];
  for (let i = 0; i + 1 < sorted.length; i += 2) {
    pairs.push({
      baseline: sorted[i],
      specimen: sorted[i + 1],
      included: true,
    });
  }
  const unpairedFiles = sorted.length % 2 !== 0 ? [sorted[sorted.length - 1]] : [];
  return { pairs, unpairedFiles };
}

async function parseFileFromBrowser(file: File): Promise<ParseResult> {
  const text = await file.text();
  let json: unknown;
  try {
    json = JSON.parse(text) as unknown;
  } catch {
    return {
      ok: false,
      error: { filename: file.name, reason: 'Invalid JSON syntax' },
    };
  }
  return parseDevMessageFile(file.name, json);
}

const STEPS = ['Upload Files', 'Review Pairs', 'Import'];

// ─── Component ────────────────────────────────────────────────────────────────

export interface ImportWizardProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful import, e.g. to refresh a test list. */
  onImportComplete?: () => void;
}

export function ImportWizard({
  open,
  onClose,
  onImportComplete,
}: ImportWizardProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [rawFileMap, setRawFileMap] = useState<Map<string, File>>(new Map());
  const [validFiles, setValidFiles] = useState<DevMessageFile[]>([]);
  const [parseErrors, setParseErrors] = useState<
    { filename: string; reason: string }[]
  >([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportBatchResult | null>(
    null,
  );
  const [importError, setImportError] = useState<string | null>(null);

  // App-export mode (single LabCloud export JSON file)
  const [labCloudExportFile, setLabCloudExportFile] = useState<LabCloudExportFile | null>(null);
  const [appExportRawFile, setAppExportRawFile] = useState<File | null>(null);

  const [pairingState, dispatch] = useReducer(pairingReducer, {
    pairs: [],
    unpairedFiles: [],
  });

  const [profiles, setProfiles] = useState<ProfileSummary[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');

  // Load profiles once when the wizard opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setProfilesLoading(true);
    profileService.getProfiles()
      .then((items) => {
        if (!cancelled) {
          setProfiles(items);
          const defaultProfile = items.find((p) => p.isDefault) ?? (items.length === 1 ? items[0] : undefined);
          if (defaultProfile) setSelectedProfileId(defaultProfile.id);
        }
      })
      .catch(() => { /* non-fatal */ })
      .finally(() => { if (!cancelled) setProfilesLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  // ── File selection ──────────────────────────────────────────────────────────

  const handleFiles = useCallback(async (fileList: FileList) => {
    const files = Array.from(fileList).filter((f) =>
      f.name.toLowerCase().endsWith('.json'),
    );
    if (files.length === 0) return;

    // When exactly one file is uploaded, check if it is a LabCloud app-export
    if (files.length === 1) {
      const text = await files[0].text();
      let json: unknown;
      let jsonParsed = false;
      try {
        json = JSON.parse(text) as unknown;
        jsonParsed = true;
      } catch { /* not valid JSON — fall through to dev-message path */ }

      if (jsonParsed) {
        const exportResult = parseLabCloudExportFile(files[0].name, json);
        if (exportResult.ok) {
          setLabCloudExportFile(exportResult.data);
          setAppExportRawFile(files[0]);
          // Clear any leftover dev-message state
          setValidFiles([]);
          setParseErrors([]);
          setRawFileMap(new Map());
          dispatch({ type: 'init', pairs: [], unpairedFiles: [] });
          return;
        }
      }
    }

    // Reset app-export state when using the dev-message path
    setLabCloudExportFile(null);
    setAppExportRawFile(null);

    const results = await Promise.all(files.map(parseFileFromBrowser));

    const newValidFiles: DevMessageFile[] = [];
    const newErrors: { filename: string; reason: string }[] = [];

    results.forEach((result) => {
      if (result.ok) {
        newValidFiles.push(result.data);
      } else {
        newErrors.push(result.error);
      }
    });

    const newRawFileMap = new Map<string, File>();
    files.forEach((f) => newRawFileMap.set(f.name, f));

    setRawFileMap(newRawFileMap);
    setValidFiles(newValidFiles);
    setParseErrors(newErrors);

    const { pairs, unpairedFiles } = autoPair(newValidFiles);
    dispatch({ type: 'init', pairs, unpairedFiles });
  }, []);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    void handleFiles(e.dataTransfer.files);
  };

  // ── Wizard navigation ───────────────────────────────────────────────────────

  const canAdvanceFromStep0 = labCloudExportFile !== null || pairingState.pairs.length > 0;
  const includedPairs = pairingState.pairs.filter((p) => p.included);
  const canAdvanceFromStep1 = labCloudExportFile !== null || includedPairs.length > 0;

  const handleNext = () => setActiveStep((s) => s + 1);
  const handleBack = () => setActiveStep((s) => s - 1);

  // ── Import submission ───────────────────────────────────────────────────────

  const handleImport = async () => {
    setImporting(true);
    setImportError(null);
    try {
      let result: ImportBatchResult;

      if (labCloudExportFile && appExportRawFile) {
        // App-export mode: single file, all tests pre-paired
        result = await importService.importFromAppExport(appExportRawFile, selectedProfileId);
      } else {
        // Dev-message mode: multiple files, user-defined pairs
        const filesToUpload: File[] = [];
        const pairs = includedPairs.map((pair) => {
          const blFile = rawFileMap.get(pair.baseline.filename);
          const spFile = rawFileMap.get(pair.specimen.filename);
          if (blFile && !filesToUpload.includes(blFile)) filesToUpload.push(blFile);
          if (spFile && !filesToUpload.includes(spFile)) filesToUpload.push(spFile);
          return {
            baselineFilename: pair.baseline.filename,
            specimenFilename: pair.specimen.filename,
          };
        });
        result = await importService.importMeasurements(filesToUpload, pairs, selectedProfileId);
      }

      setImportResult(result);
      if (result.successCount > 0 && onImportComplete) {
        onImportComplete();
      }
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'An unexpected error occurred.';
      setImportError(msg);
    } finally {
      setImporting(false);
    }
  };

  // ── Reset on close ──────────────────────────────────────────────────────────

  const handleClose = () => {
    if (importing) return; // Don't close during active upload
    setActiveStep(0);
    setRawFileMap(new Map());
    setValidFiles([]);
    setParseErrors([]);
    setImportResult(null);
    setImportError(null);
    setSelectedProfileId('');
    setLabCloudExportFile(null);
    setAppExportRawFile(null);
    dispatch({ type: 'init', pairs: [], unpairedFiles: [] });
    onClose();
  };

  // ─── Render helpers ─────────────────────────────────────────────────────────

  const renderStep0 = () => (
    <Box>
      {/* Drop zone */}
      <Box
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        aria-label="Click or drag JSON files to upload"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
        }}
        sx={{
          border: '2px dashed',
          borderColor: isDragging ? 'primary.main' : 'divider',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: isDragging ? 'action.hover' : 'background.paper',
          transition: 'all 0.15s ease',
          '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
        }}
      >
        <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body1" fontWeight={600}>
          Drag &amp; drop JSON files here
        </Typography>
        <Typography variant="body2" color="text.secondary">
          or click to browse — accepts multiple .json files
        </Typography>
      </Box>

      <Box
        component="input"
        ref={fileInputRef}
        type="file"
        multiple
        accept=".json"
        aria-label="Select JSON measurement files"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          if (e.target.files) void handleFiles(e.target.files);
        }}
        sx={{ display: 'none' }}
      />

      {/* Summary */}
      {(validFiles.length > 0 || parseErrors.length > 0 || labCloudExportFile !== null) && (
        <Stack spacing={1} mt={2}>
          {labCloudExportFile !== null && (
            <Alert
              severity="info"
              icon={<CheckCircleOutlineIcon fontSize="inherit" />}
            >
              <strong>LabCloud export detected</strong> —{' '}
              {labCloudExportFile.metadata.testCount} test
              {labCloudExportFile.metadata.testCount !== 1 ? 's' : ''} will be
              imported from <em>{labCloudExportFile.filename}</em>.
            </Alert>
          )}

          {validFiles.length > 0 && (
            <Alert
              severity="success"
              icon={<CheckCircleOutlineIcon fontSize="inherit" />}
            >
              {validFiles.length} valid file{validFiles.length !== 1 ? 's' : ''}{' '}
              → {pairingState.pairs.length} pair
              {pairingState.pairs.length !== 1 ? 's' : ''}
              {pairingState.unpairedFiles.length > 0 &&
                `, 1 unpaired (will be skipped)`}
            </Alert>
          )}

          {parseErrors.length > 0 && (
            <Alert severity="error">
              <strong>
                {parseErrors.length} file{parseErrors.length !== 1 ? 's' : ''}{' '}
                rejected:
              </strong>
              <List dense disablePadding sx={{ mt: 0.5 }}>
                {parseErrors.map((err) => (
                  <ListItem key={err.filename} disablePadding>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <ErrorOutlineIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${err.filename}: ${err.reason}`}
                      primaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}
        </Stack>
      )}
    </Box>
  );

  const renderStep1 = () => {
    if (labCloudExportFile !== null) {
      return (
        <Box>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            The following tests from the export file will be imported. All{' '}
            <strong>{labCloudExportFile.tests.length}</strong> test
            {labCloudExportFile.tests.length !== 1 ? 's' : ''} have both
            baseline and specimen data.
          </Typography>
          <List dense>
            {labCloudExportFile.tests.map((t, i) => (
              <ListItem key={t.testId} disablePadding sx={{ py: 0.25 }}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <Typography variant="caption" color="text.secondary">
                    {i + 1}.
                  </Typography>
                </ListItemIcon>
                <ListItemText
                  primary={
                    t.sourceBaseline && t.sourceSpecimen
                      ? `${t.sourceBaseline} (baseline) + ${t.sourceSpecimen} (specimen)`
                      : t.testId
                  }
                  secondary={t.notes ?? undefined}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      );
    }

    return (
      <Box>
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          Review auto-detected pairs. Use the selects to reassign files, the{' '}
          <strong>⇄</strong> button to swap within a pair, and the checkbox to
          exclude a pair from import.
        </Typography>
      <FilePairPreview
        parsedFiles={validFiles}
        pairs={pairingState.pairs}
        onTogglePair={(i) => dispatch({ type: 'toggle', index: i })}
        onSwapPair={(i) => dispatch({ type: 'swap', index: i })}
        onReassign={(pairIndex, slot, file) =>
          dispatch({ type: 'reassign', pairIndex, slot, file })
        }
        unpairedFiles={pairingState.unpairedFiles}
      />
      <Typography variant="caption" color="text.secondary" mt={1} display="block">
        {includedPairs.length} of {pairingState.pairs.length} pair
        {pairingState.pairs.length !== 1 ? 's' : ''} selected for import.
      </Typography>
    </Box>
    );
  };

  const renderStep2 = () => {
    if (importResult) {
      return (
        <Box>
          {importResult.successCount > 0 && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>{importResult.successCount} test{importResult.successCount !== 1 ? 's' : ''} created successfully.</strong>
            </Alert>
          )}
          {importResult.failedCount > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <strong>{importResult.failedCount} pair{importResult.failedCount !== 1 ? 's' : ''} failed:</strong>
              <List dense disablePadding sx={{ mt: 0.5 }}>
                {importResult.errors.map((e) => (
                  <ListItem
                    key={`${e.baselineFile}-${e.specimenFile}`}
                    disablePadding
                  >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <ErrorOutlineIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${e.baselineFile} + ${e.specimenFile}: ${e.reason}`}
                      primaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}
          {importResult.createdTests.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" gutterBottom>
                Created Tests:
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {importResult.createdTests.map((t) => (
                  <Chip
                    key={t.testId}
                    label={t.testId.slice(0, 8)}
                    title={`${t.testId} (${t.baselineFile} + ${t.specimenFile})`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </>
          )}
        </Box>
      );
    }

    return (
      <Box>
        {importError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {importError}
          </Alert>
        )}

        {/* Profile selector */}
        <FormControl fullWidth sx={{ mb: 2 }} required>
          <InputLabel id="import-profile-label">Measurement Profile</InputLabel>
          <Select
            labelId="import-profile-label"
            label="Measurement Profile"
            value={selectedProfileId}
            onChange={(e) => setSelectedProfileId(e.target.value)}
            disabled={profilesLoading || importing}
            endAdornment={
              profilesLoading ? (
                <CircularProgress size={18} sx={{ mr: 2 }} />
              ) : undefined
            }
          >
            {profiles.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="body1" mb={2}>
          Ready to import{' '}
          {labCloudExportFile !== null ? (
            <strong>
              {labCloudExportFile.tests.length} test
              {labCloudExportFile.tests.length !== 1 ? 's' : ''} from{' '}
              <em>{labCloudExportFile.filename}</em>
            </strong>
          ) : (
            <strong>{includedPairs.length} test pair{includedPairs.length !== 1 ? 's' : ''}</strong>
          )}
          :
        </Typography>
        <List dense>
          {labCloudExportFile !== null
            ? labCloudExportFile.tests.map((t, i) => (
                <ListItem key={t.testId} disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Typography variant="caption" color="text.secondary">
                      {i + 1}.
                    </Typography>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      t.sourceBaseline && t.sourceSpecimen
                        ? `${t.sourceBaseline} (baseline) + ${t.sourceSpecimen} (specimen)`
                        : t.testId
                    }
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))
            : includedPairs.map((pair, i) => (
                <ListItem key={`${pair.baseline.filename}-${pair.specimen.filename}`} disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Typography variant="caption" color="text.secondary">
                      {i + 1}.
                    </Typography>
                  </ListItemIcon>
                  <ListItemText
                    primary={`${pair.baseline.filename} (baseline) + ${pair.specimen.filename} (specimen)`}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
        </List>
        {importing && (
          <Box mt={2}>
            <LinearProgress />
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mt={0.5}
            >
              Uploading…
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const isDone = importResult !== null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="import-wizard-title"
    >
      <DialogTitle id="import-wizard-title">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Import Measurement Files</Typography>
          <IconButton
            aria-label="Close import wizard"
            onClick={handleClose}
            size="small"
            disabled={importing}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <Box px={3} pb={1}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent dividers>
        {activeStep === 0 && renderStep0()}
        {activeStep === 1 && renderStep1()}
        {activeStep === 2 && renderStep2()}
      </DialogContent>

      <DialogActions>
        {!isDone && (
          <Button onClick={handleClose} disabled={importing}>
            Cancel
          </Button>
        )}

        {activeStep > 0 && !isDone && (
          <Button onClick={handleBack} disabled={importing}>
            Back
          </Button>
        )}

        {activeStep === 0 && (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canAdvanceFromStep0}
          >
            Next
          </Button>
        )}

        {activeStep === 1 && (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canAdvanceFromStep1}
          >
            Next
          </Button>
        )}

        {activeStep === 2 && !isDone && (() => {
          const isAppExport = labCloudExportFile !== null;
          const testCount = isAppExport
            ? labCloudExportFile.tests.length
            : includedPairs.length;
          const label = isAppExport
            ? `Import ${testCount} Test${testCount !== 1 ? 's' : ''}`
            : `Import ${testCount} Pair${testCount !== 1 ? 's' : ''}`;
          return (
            <Button
              variant="contained"
              color="primary"
              onClick={() => void handleImport()}
              disabled={importing || testCount === 0 || !selectedProfileId}
            >
              {importing ? 'Importing…' : label}
            </Button>
          );
        })()}

        {isDone && (
          <Button variant="contained" onClick={handleClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
